import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import DateRangeFilter from "components/common/DateRangeFilter";
import GraphCard from "components/dashboard/GraphCard";
import FullPageLoader from "components/common/FullPageLoader";
import { DashboardSelector, fetchDashboardGraph } from "slices/dashboardSlice";
import { Controller, useForm } from "react-hook-form";

function Reports() {
    const dispatch = useDispatch();
    const { dashboard, loader } = useSelector(DashboardSelector);
    const [dateRange, setDateRange] = useState({
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    });

    const { control, watch } = useForm({
        defaultValues: {
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        }
    });

    const startDate = watch('start_date');
    const endDate = watch('end_date');

    // Get dates from backend and format as "MMM DD YYYY"
    const backendDates = dashboard?.graph?.dates || [];

    const dateCategories = backendDates.map(dateStr => {
        return moment(dateStr).format('MMM DD YYYY');
    });

    // Handle search button click
    const handleSearch = () => {
        const params = {
            start_date: moment(startDate).format('YYYY-MM-DD'),
            end_date: moment(endDate).format('YYYY-MM-DD'),
        };
        setDateRange(params);
        dispatch(fetchDashboardGraph(params));
    };

    // Initial load on component mount
    useEffect(() => {
        const params = {
            start_date: moment().format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        };
        setDateRange(params);
        dispatch(fetchDashboardGraph(params));
    }, [dispatch]);

    const graphData = dashboard?.graph;

    // interpreter request
    const type0Completed = graphData?.types?.[0]?.map(item => item.completed);
    const type0Assigned = graphData?.types?.[0]?.map(item => item.assigned);
    const type0Declined = graphData?.types?.[0]?.map(item => item.declined);
    const type0Cancelled = graphData?.types?.[0]?.map(item => item.cancelled);
    const type0Pending = graphData?.types?.[0]?.map(item => item.pending);
    const type0Available = graphData?.types?.[0]?.map(item => item.available);

    // interpreter requests for evening school
    const type1Completed = graphData?.types?.[1]?.map(item => item.completed);
    const type1Assigned = graphData?.types?.[1]?.map(item => item.assigned);
    const type1Declined = graphData?.types?.[1]?.map(item => item.declined);
    const type1Cancelled = graphData?.types?.[1]?.map(item => item.cancelled);
    const type1Pending = graphData?.types?.[1]?.map(item => item.pending);
    const type1Available = graphData?.types?.[1]?.map(item => item.available);

    // Bilingual Exam
    const type2Completed = graphData?.types?.[2]?.map(item => item.completed);
    const type2Assigned = graphData?.types?.[2]?.map(item => item.assigned);
    const type2Declined = graphData?.types?.[2]?.map(item => item.declined);
    const type2Cancelled = graphData?.types?.[2]?.map(item => item.cancelled);
    const type2Pending = graphData?.types?.[2]?.map(item => item.pending);
    const type2Available = graphData?.types?.[2]?.map(item => item.available);

    // Translation Request
    const type3Completed = graphData?.types?.[3]?.map(item => item.completed);
    const type3Assigned = graphData?.types?.[3]?.map(item => item.assigned);
    const type3Declined = graphData?.types?.[3]?.map(item => item.declined);
    const type3Cancelled = graphData?.types?.[3]?.map(item => item.cancelled);
    const type3Pending = graphData?.types?.[3]?.map(item => item.pending);
    const type3Available = graphData?.types?.[3]?.map(item => item.available);

    return (
        <>
            <div className="content">
                <div className="row_main_head">
                    <h2 className="main_heading">Reports</h2>
                </div>

                {/* Date Range Filter */}
                <div className="card_table p-4 mb-4">
                    <div className="row-primary dahboard_filter reports">
                        <DateRangeFilter control={control} />
                        <button
                            type="button"
                            className="main_button green"
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </div>
                </div>

                {/* Graphs */}
                <div className="row_dash_1">
                    <div className="card_table p-4">
                        <GraphCard
                            title="Interpreter Requests"
                            categories={dateCategories}
                            seriesData={[
                                { name: "Completed", data: type0Completed },
                                { name: "Assigned", data: type0Assigned },
                                { name: "Declined", data: type0Declined },
                                { name: "Cancelled", data: type0Cancelled },
                                { name: "Pending", data: type0Pending },
                                { name: "Available", data: type0Available },
                            ]}
                        />
                    </div>

                    <div className="card_table p-4">
                        <GraphCard
                            title="Interpreter Requests for Evening School Events"
                            categories={dateCategories}
                            seriesData={[
                                { name: "Completed", data: type1Completed },
                                { name: "Assigned", data: type1Assigned },
                                { name: "Declined", data: type1Declined },
                                { name: "Cancelled", data: type1Cancelled },
                                { name: "Pending", data: type1Pending },
                                { name: "Available", data: type1Available },
                            ]}
                        />
                    </div>
                </div>

                <div className="row_dash_1">
                    <div className="card_table p-4">
                        <GraphCard
                            title="Bilingual Exams"
                            categories={dateCategories}
                            seriesData={[
                                { name: "Completed", data: type2Completed },
                                { name: "Assigned", data: type2Assigned },
                                { name: "Declined", data: type2Declined },
                                { name: "Cancelled", data: type2Cancelled },
                                { name: "Pending", data: type2Pending },
                                { name: "Available", data: type2Available },
                            ]}
                        />
                    </div>
                    <div className="card_table p-4">
                        <GraphCard
                            title="Translation Requests"
                            categories={dateCategories}
                            seriesData={[
                                { name: "Completed", data: type3Completed },
                                { name: "Assigned", data: type3Assigned },
                                { name: "Declined", data: type3Declined },
                                { name: "Cancelled", data: type3Cancelled },
                                { name: "Pending", data: type3Pending },
                                { name: "Available", data: type3Available },
                            ]}
                        />
                    </div>
                </div>
            </div>
            {loader && <FullPageLoader />}
        </>
    );
}

export default Reports;
